/**
 * The Beam Shop: cart drawer upsell row.
 * Fills <beam-cart-upsell> (snippets/beam-cart-upsell.liquid) with cards rendered by
 * sections/beam-cart-upsell.liquid through Shopify's product recommendations API, and
 * handles one-tap "Add" so the drawer refreshes the same way Horizon's own add-to-cart does.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const DRAWER_SECTION_ID = 'cart-drawer-section';
const cache = new Map();

class BeamCartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-cart-product-ids'];

  #token = 0;

  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.#load();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    // Fires when the drawer re-renders after a cart change.
    if (oldValue !== null && oldValue !== newValue && this.isConnected) this.#load();
  }

  async #load() {
    const productId = this.dataset.productId;
    if (!productId) return;

    const key = `${productId}|${this.dataset.cartProductIds || ''}`;
    const token = ++this.#token;

    try {
      let html = cache.get(key);
      if (!html) {
        const root = window.Shopify?.routes?.root || '/';
        const url = `${root}recommendations/products?product_id=${encodeURIComponent(productId)}&limit=10&intent=related&section_id=beam-cart-upsell`;
        const response = await fetch(url);
        if (!response.ok) throw new Error(`Recommendations request failed: ${response.status}`);
        html = await response.text();
        cache.set(key, html);
      }
      if (token !== this.#token) return; // a newer load has started

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const inner = doc.querySelector('.beam-upsell__inner');
      const hasCards = inner && inner.querySelector('.beam-upsell__card');
      this.replaceChildren(...(hasCards ? [document.importNode(inner, true)] : []));
      if (hasCards) this.#setupNav();
    } catch (error) {
      console.warn('[beam-cart-upsell]', error);
    }
  }

  /** Desktop arrows: shown only when the row overflows; greyed out at either end. */
  #setupNav() {
    const track = this.querySelector('.beam-upsell__track');
    const nav = this.querySelector('.beam-upsell__nav');
    if (!track || !nav) return;
    const [prev, next] = nav.querySelectorAll('.beam-upsell__arrow');
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      nav.hidden = max <= 1;
      if (prev) prev.disabled = track.scrollLeft <= 1;
      if (next) next.disabled = track.scrollLeft >= max - 1;
    };
    track.addEventListener('scroll', update, { passive: true });
    new ResizeObserver(update).observe(track);
    update();
  }

  /** @param {HTMLElement} arrow */
  #scrollRow(arrow) {
    const track = this.querySelector('.beam-upsell__track');
    const card = track?.querySelector('.beam-upsell__card');
    if (!track || !card) return;
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const step = (card.getBoundingClientRect().width + gap) * 2;
    track.scrollBy({ left: Number(arrow.dataset.dir) * step, behavior: 'smooth' });
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const arrow = event.target instanceof Element ? event.target.closest('.beam-upsell__arrow') : null;
    if (arrow) {
      event.preventDefault();
      this.#scrollRow(arrow);
      return;
    }
    const button = event.target instanceof Element ? event.target.closest('button[data-variant-id]') : null;
    if (!button || button.disabled) return;
    event.preventDefault();
    this.#add(button);
  };

  /** @param {HTMLButtonElement} button */
  #add(button) {
    const variantId = button.dataset.variantId;
    const label = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';

    const promise = (async () => {
      const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: DRAWER_SECTION_ID,
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Add to cart failed');

      const cartResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`);
      const ajaxCart = await cartResponse.json();

      return {
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          sections: data.sections,
          source: 'beam-cart-upsell',
          didError: false,
        },
      };
    })();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise,
      })
    );

    promise.catch((error) => {
      console.warn('[beam-cart-upsell]', error);
      button.textContent = 'Unavailable';
      setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 2500);
    });
  }
}

if (!customElements.get('beam-cart-upsell')) customElements.define('beam-cart-upsell', BeamCartUpsell);
